import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { AppTooltip } from "@components/AppTooltip";
import type { Database } from "@hvf/contracts";
import { useNavigate } from "@solidjs/router";
import { ArrowRight, Check, Lock, Play } from "lucide-solid";
import { Match, Show, Switch, createMemo } from "solid-js";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { getCourseDecor } from "../courseDecor";
import { useCourses } from "../useCourses";

type CourseRow = Database['public']['Functions']['get_courses_with_progress']['Returns'][0]

export default function ({ course }: { course: CourseRow }) {
  const navigate = useNavigate();
  const { coursesQuery } = useCourses();
  const decor = getCourseDecor(course.title);

  const prerequisite = createMemo(() => {
    if (!course.prerequisite_slug) return undefined;
    return coursesQuery.data?.find((c) => c.slug === course.prerequisite_slug);
  });

  const isLocked = createMemo(() => {
    const prereq = prerequisite();
    if (!prereq) return false;
    return prereq.completed_lessons < prereq.total_lessons;
  });

  const percent = createMemo(() =>
    course.total_lessons ? Math.round((course.completed_lessons / course.total_lessons) * 100) : 0
  );

  const status = createMemo<'locked' | 'completed' | 'progress' | 'open'>(() => {
    if (isLocked()) return 'locked';
    if (course.total_lessons > 0 && course.completed_lessons >= course.total_lessons) return 'completed';
    if (course.completed_lessons > 0) return 'progress';
    return 'open';
  });

  const go = () => {
    if (status() === 'locked') return;
    navigate(`/courses/${course.slug}`);
  };

  return (
    <Grid
      role="button"
      tabIndex={status() === 'locked' ? -1 : 0}
      aria-label={course.title}
      onClick={go}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && go()}
      gridTemplateColumns={{ base: '46px 1fr auto', smDown: '42px 1fr' }}
      columnGap={{ base: '15px', smDown: '12px' }}
      rowGap="10px"
      alignItems="center"
      bg="editorialPanel"
      border="1px solid var(--colors-editorial-hair)"
      borderRadius="13px"
      p={{ base: '14px 17px', smDown: '12px 14px' }}
      cursor={status() === 'locked' ? 'default' : 'pointer'}
      style={{ '--c': decor.color }}
      class={css({
        boxShadow: 'inset 3px 0 0 var(--c), 0 1px 2px rgba(20,22,27,.04)',
        transition: 'transform .2s, box-shadow .2s, border-color .2s',
        '&:hover': status() === 'locked' ? {} : {
          transform: 'translateY(-2px)',
          borderColor: 'var(--colors-editorial-hair-strong)',
          boxShadow: 'inset 3px 0 0 var(--c), var(--shadows-editorial-lift)',
        },
      })}
    >
      <Flex
        width={{ base: '46px', smDown: '42px' }}
        height={{ base: '46px', smDown: '42px' }}
        borderRadius="11px"
        align="center"
        justify="center"
        flex="none"
        background="color-mix(in srgb, var(--c) 15%, var(--colors-editorial-panel))"
        color="var(--c)"
        filter={status() === 'locked' ? 'saturate(.55)' : 'none'}
        opacity={status() === 'locked' ? 0.8 : 1}
      >
        <decor.icon size={22} />
      </Flex>

      <Box minWidth="0">
        <Text
          fontSize="15px"
          fontWeight="700"
          letterSpacing="-.01em"
          color={status() === 'locked' ? 'editorialInk2' : 'editorialInk'}
          mb="3px"
        >
          {course.title}
        </Text>
        <Text
          fontSize="12.5px"
          color="editorialInk2"
          lineHeight="1.4"
          mb="9px"
          lineClamp={1}
        >
          {course.description}
        </Text>

        <Show
          when={status() === 'locked'}
          fallback={
            <Flex gap="10px" align="center">
              <Box
                width="180px"
                maxWidth="42vw"
                height="5px"
                borderRadius="999px"
                background="color-mix(in srgb, var(--colors-editorial-ink) 11%, transparent)"
                overflow="hidden"
                flex="none"
              >
                <Box height="100%" borderRadius="999px" bg="editorialAccent" style={{ width: `${percent()}%` }} />
              </Box>
              <Text fontSize="11.5px" color="editorialInk3">
                {course.completed_lessons} of {course.total_lessons} lessons
              </Text>
            </Flex>
          }
        >
          <Flex gap="6px" align="center" fontSize="11.5px" color="editorialInk3">
            <Lock size={13} />
            Unlocks after {prerequisite()?.title}
          </Flex>
        </Show>
      </Box>

      <Flex
        direction="column"
        align="stretch"
        gap="7px"
        flex="none"
        gridColumn={{ base: 'auto', smDown: '1 / -1' }}
        justifySelf={{ base: 'auto', smDown: 'start' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Switch>
          <Match when={status() === 'progress'}>
            <Button
              type="button"
              bg="editorialAccent"
              color="editorialBadgeFg"
              h="36px"
              px="15px"
              borderRadius="9px"
              fontWeight="700"
              fontSize="12.5px"
              _hover={{ filter: 'brightness(1.04)' }}
              onClick={go}
            >
              <Play size={14} fill="currentColor" />
              Continue
            </Button>
          </Match>

          <Match when={status() === 'locked'}>
            <AppTooltip content={`Complete ${prerequisite()?.title ?? 'the previous course'} to unlock`}>
              <Flex
                align="center"
                gap="6px"
                h="36px"
                px="14px"
                border="1px solid var(--colors-editorial-hair-strong)"
                borderRadius="9px"
                bg="editorialPanel"
                color="editorialInk3"
                fontSize="12.5px"
                fontWeight="600"
              >
                <Lock size={14} />
                Locked
              </Flex>
            </AppTooltip>
          </Match>

          <Match when={status() === 'completed'}>
            <Button
              type="button"
              bg="editorialBtn2Bg"
              color="editorialBtn2Fg"
              h="36px"
              px="15px"
              borderRadius="9px"
              fontWeight="700"
              fontSize="12.5px"
              _hover={{ bg: 'editorialBtn2Hover' }}
              onClick={go}
            >
              Review
              <ArrowRight size={14} />
            </Button>
            <Flex
              align="center"
              justify="center"
              gap="6px"
              h="36px"
              px="14px"
              borderRadius="9px"
              background="color-mix(in srgb, var(--colors-editorial-accent) 15%, transparent)"
              border="1px solid color-mix(in srgb, var(--colors-editorial-accent) 40%, transparent)"
              color="editorialAccentDeep"
              fontSize="12px"
              fontWeight="700"
              letterSpacing=".04em"
              textTransform="uppercase"
            >
              <Check size={14} />
              Completed
            </Flex>
          </Match>

          <Match when={status() === 'open'}>
            <Button
              type="button"
              bg="editorialBtn2Bg"
              color="editorialBtn2Fg"
              h="36px"
              px="15px"
              borderRadius="9px"
              fontWeight="700"
              fontSize="12.5px"
              _hover={{ bg: 'editorialBtn2Hover' }}
              onClick={go}
            >
              Overview
              <ArrowRight size={14} />
            </Button>
          </Match>
        </Switch>
      </Flex>
    </Grid>
  )
}
